import type { Route } from "./+types/$locale.faq";
import { buildSeoMeta } from "@/components/seo/meta";
import { faqSchema } from "@/components/seo/structuredData";
import { DEFAULT_FAQ } from "@/features/support/defaults/faqDefaults";
import { FAQPage } from "@/features/support/routes/FAQPage";

/**
 * No loader: FAQ content is sourced via `usePageSection('faq', 'content')` at
 * runtime with a graceful fallback to `DEFAULT_FAQ`. There is currently no
 * server-side API to fetch FAQ content from at build time (the CMS surfaces
 * it via the per-locale static manifest), so the meta export builds the
 * FAQPage JSON-LD from the same DEFAULT_FAQ the component falls back to.
 * This guarantees rich-result-eligible markup even before CMS publishes any
 * FAQ overrides; once the CMS does publish, the runtime hook still wins for
 * the on-page content.
 */

const DEFAULT_FAQ_ENTRIES = DEFAULT_FAQ.categories.flatMap((category) =>
    category.faqs.map((faq) => ({ question: faq.question, answer: faq.answer })),
);

export function meta({ params }: Route.MetaArgs) {
    const locale = (params.locale as string) ?? "en";
    return buildSeoMeta({
        title: "FAQ — Frequently Asked Questions",
        description:
            "Answers to common questions about Stork Rentals: bookings, pickup and delivery, cleaning, deposits, cancellations and monthly rentals.",
        path: "/faq",
        // TODO Phase 6: hydrate availableLocales from root loader manifest
        locale,
        availableLocales: [{ code: "en" }],
        jsonLd: DEFAULT_FAQ_ENTRIES.length ? faqSchema(DEFAULT_FAQ_ENTRIES) : undefined,
    });
}

export default function FAQRoute() {
    return <FAQPage />;
}
